import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Mic, MicOff } from 'lucide-react';
import useVoiceDictation from '../hooks/useVoiceDictation';
import FAQ from './FAQ';
import BuyButton from './BuyButton';
import { usePayment } from '../contexts/PaymentContext';
import { trackEvent } from '../services/analytics'
import { FUNNEL, trackFunnel } from '../lib/funnel'
import { supabase } from '../services/supabase'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import Auth from './Auth'
import CommunityPromptsPreview from './CommunityPromptsPreview'
import { getUTMParamsForTracking } from '../utils/utm'

const DESIGN_STYLES = ['Minimalist', 'Modern', 'Bold', 'Playful', 'Corporate']
const TARGET_PLATFORMS = ['Cursor', 'Lovable', 'Claude', 'ChatGPT', 'Bolt', 'Replit']

// Quick-pick example apps that prefill the textarea — lowers blank-canvas friction.
// Picked to feel concrete + buildable in a weekend, not "the next Uber".
const EXAMPLE_APPS = [
  {
    emoji: '✅',
    label: 'Habit tracker',
    prompt: 'A habit tracker with daily streaks, reminders, friend accountability, and a shareable progress card. Users can create up to 5 habits, mark them done each day, and see week/month heatmaps.',
    type: 'Web App',
  },
  {
    emoji: '💸',
    label: 'Budget app',
    prompt: 'A personal finance app that imports bank transactions, auto-categorises spending, sets monthly budgets per category, and sends alerts when nearing limits. Includes a clean dashboard with charts.',
    type: 'Web App',
  },
  {
    emoji: '🍳',
    label: 'Recipe finder',
    prompt: 'A recipe app where users enter ingredients they have and get matching recipes ranked by completeness. Save favourites, generate shopping lists, and rate dishes after cooking.',
    type: 'Mobile App',
  },
  {
    emoji: '🏋️',
    label: 'Workout planner',
    prompt: 'A workout planner that builds weekly routines based on goals (strength, hypertrophy, fat loss), available equipment, and time per session. Logs sets/reps and tracks PRs over time.',
    type: 'Mobile App',
  },
  {
    emoji: '📚',
    label: 'Reading log',
    prompt: 'A reading-tracker app where users log books, set yearly goals, write notes per chapter, and get a year-in-books summary. Optional Goodreads import.',
    type: 'Web App',
  },
  {
    emoji: '🧘',
    label: 'Mood journal',
    prompt: 'A daily mood-journal app with quick check-ins (emoji + 1-5 rating), free-text reflections, gentle weekly insights, and private cloud sync. Calm, minimalist UI.',
    type: 'Mobile App',
  },
]

const Hero = ({ onNavigateToFeature }) => {
  const [communityCount, setCommunityCount] = useState(0)
  const [displayCount, setDisplayCount] = useState(0)
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { hasAccess, accessTier } = usePayment()
  const showBuyCta = !hasAccess || accessTier === 'trial'

  // Main Form State
  const [heroProjectType, setHeroProjectType] = useState('Web App')
  const [heroAppIdea, setHeroAppIdea] = useState('')
  const [heroDesignStyle, setHeroDesignStyle] = useState('Minimalist')
  const [heroTargetPlatform, setHeroTargetPlatform] = useState('Cursor')
  const [showAuthModal, setShowAuthModal] = useState(false)
  const [pendingProjectCreation, setPendingProjectCreation] = useState(false)

  // Voice-to-text dictation (Web Speech API). On unsupported browsers we hide the mic.
  const dictation = useVoiceDictation()
  const [listenDots, setListenDots] = useState(1)

  const toggleDictation = () => {
    if (dictation.isRecording) {
      dictation.stop()
      return
    }
    dictation.start({
      onResult: (chunk) => {
        // Append final chunks to whatever the user already has.
        setHeroAppIdea((prev) => (prev ? prev.trim() + ' ' : '') + chunk)
      },
    })
  }

  // Animate the "Listening..." placeholder dots while recording.
  useEffect(() => {
    if (!dictation.isRecording) return
    const id = setInterval(() => setListenDots((d) => (d % 3) + 1), 500)
    return () => clearInterval(id)
  }, [dictation.isRecording])

  const PREFILL_KEY = 'project_creation_prefill'

  // --- HANDLERS ---

  const handlePickExample = (example) => {
    setHeroAppIdea(example.prompt)
    if (example.type) setHeroProjectType(example.type)
    try {
      trackEvent('hero_example_picked', { label: example.label, type: example.type })
      trackFunnel(FUNNEL.HERO_VIEW, { interaction: 'example_picked', label: example.label })
    } catch {}
    // Focus the textarea so the user sees the prefilled content + can edit
    requestAnimationFrame(() => {
      const ta = document.querySelector('textarea[name="hero-idea"]') || document.querySelector('textarea')
      if (ta) {
        ta.focus()
        ta.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }

  const handleHeroCreateSubmit = (e) => {
    e.preventDefault()
    if (!heroAppIdea.trim()) return

    const autoProjectName = heroAppIdea.trim().split(/\s+/).slice(0, 5).join(' ')

    try {
      localStorage.setItem(PREFILL_KEY, JSON.stringify({
        projectName: autoProjectName,
        appIdea: heroAppIdea.trim(),
        projectType: heroProjectType,
        designStyle: heroDesignStyle,
        targetPlatform: heroTargetPlatform
      }))
    } catch {}

    const utmParams = getUTMParamsForTracking()
    trackEvent('continue button clicked', {
      project_type: heroProjectType,
      design_style: heroDesignStyle,
      target_platform: heroTargetPlatform,
      is_authenticated: isAuthenticated,
      ...utmParams
    })
    trackFunnel(FUNNEL.HERO_FORM_SUBMITTED, {
      project_type: heroProjectType,
      target_platform: heroTargetPlatform,
      design_style: heroDesignStyle,
      is_authenticated: isAuthenticated,
      idea_length: heroAppIdea.trim().length,
    })

    if (isAuthenticated) {
      navigate('/create-project')
    } else {
      setPendingProjectCreation(true)
      setShowAuthModal(true)
    }
  }

  // --- EFFECTS ---

  useEffect(() => {
    if (pendingProjectCreation && isAuthenticated) {
      setPendingProjectCreation(false)
      setShowAuthModal(false)
      navigate('/create-project')
    }
  }, [pendingProjectCreation, isAuthenticated, navigate])

  // Fire one-time hero view event
  useEffect(() => {
    trackFunnel(FUNNEL.HERO_VIEW, { is_authenticated: isAuthenticated })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Community Count Animation
  useEffect(() => {
    let cancelled = false
    const fetchCount = async () => {
      try {
        const { count } = await supabase.from('profiles').select('*', { count: 'exact', head: true })
        if (cancelled) return
        const raw = typeof count === 'number' ? count : null
        const total = (raw && raw >= 1000) ? raw : 1000
        setCommunityCount(total)
        const start = performance.now()
        const duration = 900
        const step = (t) => {
          if (cancelled) return
          const p = Math.min(1, (t - start) / duration)
          const eased = 1 - Math.pow(1 - p, 3)
          const val = Math.floor(0 + (total - 0) * eased)
          setDisplayCount(val)
          if (p < 1) requestAnimationFrame(step)
        }
        requestAnimationFrame(step)
      } catch (e) {
        if (!cancelled) { setCommunityCount(1000); setDisplayCount(1000); }
      }
    }
    fetchCount()
    return () => { cancelled = true }
  }, [])

  return (
    <div className="min-h-screen bg-white relative">

      {/* ==================== HERO SECTION ==================== */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="relative text-center pt-10 pb-12 sm:py-24 px-4 overflow-hidden"
      >
        {/* Background blobs */}
        <div className="absolute inset-0 -z-0">
          <motion.div
            className="absolute top-[-10rem] left-[-10rem] w-[30rem] h-[30rem] bg-vibe-cyan/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.1, 1], rotate: [0, 10, 0] }}
            transition={{ duration: 20, repeat: Infinity, repeatType: "mirror" }}
          />
          <motion.div
            className="absolute bottom-[-10rem] right-[-10rem] w-[30rem] h-[30rem] bg-blue-200/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.05, 1], rotate: [0, -10, 0] }}
            transition={{ duration: 25, repeat: Infinity, repeatType: "mirror", delay: 5 }}
          />
        </div>

        <div className="relative z-10">
          {/* Trust line — concrete signal above the fold */}
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="flex justify-center mb-3 sm:mb-4 px-2"
          >
            <div className="inline-flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs text-gray-600">
              <span className="text-amber-500 tracking-tight" aria-hidden>★★★★★</span>
              <span>
                <span className="font-semibold text-gray-900">{displayCount.toLocaleString()}+</span>{' '}
                builders shipping with{' '}
                <span className="font-semibold text-gray-900">Cursor, Claude Code & Lovable</span>
              </span>
            </div>
          </motion.div>

          <h1 className="font-bold text-[28px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-5xl xl:text-6xl text-black max-w-4xl mx-auto mb-4 sm:mb-6 tracking-tight px-2">
            <span className="block">Turn Rough Ideas into</span>
            <span className="block mt-2 md:mt-3">
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Build-Ready Prompts</span>
                <span className="absolute -bottom-1 left-0 w-full h-1 bg-gradient-to-r from-teal-400 to-cyan-400 rounded-full" />
              </span>
            </span>
          </h1>
          <p className="text-sm sm:text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed px-3">
            Describe your app once, refine it in chat, and ship it in one shot. Works with{' '}
            <span className="font-semibold text-gray-800">Cursor, Claude Code, Lovable, and ChatGPT</span>.
          </p>

          {/* Input form with glow */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-5 sm:mt-6"
          >
            <div className="relative max-w-3xl mx-auto">
              {/* Animated gradient glow — inner border */}
              <div
                className="absolute -inset-[2px] rounded-2xl opacity-75 blur-sm animate-glow-spin"
                style={{
                  background: 'linear-gradient(270deg, #22d3ee, #0ea5e9, #a78bfa, #22d3ee)',
                  backgroundSize: '300% 300%',
                }}
              />
              {/* Animated gradient glow — outer diffuse */}
              <div
                className="absolute -inset-2 rounded-2xl opacity-30 blur-xl animate-glow-spin"
                style={{
                  background: 'linear-gradient(270deg, #22d3ee, #0ea5e9, #a78bfa, #22d3ee)',
                  backgroundSize: '300% 300%',
                }}
              />

              <form
                onSubmit={handleHeroCreateSubmit}
                className="relative bg-white border border-gray-200/80 rounded-2xl shadow-lg overflow-hidden z-10"
              >
                <div className="relative group">
                  <textarea
                    name="hero-idea"
                    value={heroAppIdea}
                    onChange={(e) => setHeroAppIdea(e.target.value)}
                    placeholder={
                      dictation.isRecording
                        ? `Listening${'.'.repeat(listenDots)} speak your idea`
                        : "e.g. A habit tracker with streaks, reminders, and social accountability..."
                    }
                    rows={4}
                    className="w-full pl-4 sm:pl-6 pr-14 sm:pr-16 py-4 sm:py-5 text-gray-800 text-base sm:text-lg placeholder-gray-400 bg-transparent border-0 focus:ring-0 focus:outline-none resize-none"
                    required
                  />

                  {/* Mic button — Web Speech API dictation */}
                  {dictation.isSupported && (
                    <button
                      type="button"
                      onClick={toggleDictation}
                      aria-label={dictation.isRecording ? 'Stop voice input' : 'Start voice input'}
                      title={dictation.isRecording ? 'Stop recording' : 'Speak your idea'}
                      className={`absolute right-3 sm:right-4 bottom-3 sm:bottom-4 inline-flex items-center justify-center w-10 h-10 rounded-full border transition-all ${
                        dictation.isRecording
                          ? 'bg-red-50 border-red-300 text-red-600 hover:bg-red-100'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-vibe-cyan hover:text-black hover:bg-vibe-cyan/5'
                      }`}
                    >
                      {dictation.isRecording ? (
                        <>
                          <motion.span
                            animate={{ scale: [1, 1.4, 1], opacity: [1, 0.6, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                            className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500"
                            aria-hidden
                          />
                          <Mic className="w-4 h-4" />
                        </>
                      ) : (
                        <Mic className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>

                {/* Subtle mic error / permission hint */}
                {dictation.error && (
                  <div className="px-4 sm:px-6 py-1.5 text-[11px] text-red-600 bg-red-50/60">
                    {dictation.error}
                  </div>
                )}

                {/* In-form example chips — show only when textarea is empty.
                    Tap to start, no scrolling needed. */}
                <AnimatePresence initial={false}>
                  {!heroAppIdea.trim() && !dictation.isRecording && (
                    <motion.div
                      key="example-chips"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden border-t border-gray-100 bg-white"
                    >
                      <div className="px-3 sm:px-4 py-3 flex gap-2 overflow-x-auto flex-nowrap scrollbar-thin">
                        {EXAMPLE_APPS.map((ex) => (
                          <button
                            key={ex.label}
                            type="button"
                            onClick={() => handlePickExample(ex)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm text-gray-700 font-medium whitespace-nowrap shadow-sm hover:border-vibe-cyan hover:bg-vibe-cyan/5 hover:text-black active:scale-[0.97] transition-all"
                          >
                            <span aria-hidden>{ex.emoji}</span>
                            <span>{ex.label}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Bottom toolbar */}
                <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-gray-100 bg-gray-50/50">
                  {/* Target Platform Dropdown */}
                  <div className="relative">
                    <select
                      value={heroTargetPlatform}
                      onChange={(e) => setHeroTargetPlatform(e.target.value)}
                      className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-vibe-cyan/30 focus:border-vibe-cyan transition-all"
                    >
                      {TARGET_PLATFORMS.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Design Style Dropdown */}
                  <div className="relative">
                    <select
                      value={heroDesignStyle}
                      onChange={(e) => setHeroDesignStyle(e.target.value)}
                      className="appearance-none bg-white border border-gray-200 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-gray-700 cursor-pointer hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-vibe-cyan/30 focus:border-vibe-cyan transition-all"
                    >
                      {DESIGN_STYLES.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Web/Mobile Toggle */}
                  <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 gap-1">
                    <button
                      type="button"
                      onClick={() => setHeroProjectType('Web App')}
                      className={`flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        heroProjectType === 'Web App'
                          ? 'bg-vibe-cyan/20 text-gray-900'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Web
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeroProjectType('Mobile App')}
                      className={`flex items-center justify-center px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                        heroProjectType === 'Mobile App'
                          ? 'bg-vibe-cyan/20 text-gray-900'
                          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      Mobile
                    </button>
                  </div>

                  {/* Spacer */}
                  <div className="hidden sm:block flex-1" />

                  {/* Generate button */}
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!heroAppIdea.trim()}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-gray-900 text-white rounded-lg font-semibold text-sm hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    {isAuthenticated ? 'Generate Enhanced Prompt' : 'Sign up to Generate'}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Trust badge — sits directly under the text box */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-4 sm:mt-5 px-2"
          >
            <div className="inline-flex items-center gap-2 sm:gap-3 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-full px-3 sm:px-4 py-2 shadow-sm">
              <span className="text-base sm:text-lg flex-shrink-0" aria-hidden>👩‍💻👨‍💻</span>
              <span className="text-xs sm:text-sm font-medium text-gray-700 whitespace-nowrap">
                Join <span className="font-semibold text-gray-900">{displayCount.toLocaleString()}+</span> vibe coders
              </span>
            </div>
          </motion.div>

          {/* Secondary buy CTA — alternative to the long form path */}
          {showBuyCta && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 1 }}
              className="mt-5 flex flex-col items-center gap-2"
            >
              <div className="text-xs text-gray-500 uppercase tracking-wider">or skip the form</div>
              <BuyButton planId="trial" context="hero-secondary" variant="accent" />
              <button
                type="button"
                onClick={() => navigate('/pricing')}
                className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2"
              >
                See all plans
              </button>
            </motion.div>
          )}
        </div>
      </motion.section>

      {/* ==================== BEFORE / AFTER COMPARISON ==================== */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-gray-50"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-sm font-bold tracking-wider uppercase text-gray-500 bg-gray-200 py-1 px-3 rounded-full">Without vs With our tool</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4">
              A better first prompt changes everything
            </h2>
            <p className="text-lg text-gray-600 mt-4 max-w-2xl mx-auto">
              Compare what you get from a rough idea vs an optimized prompt generated by our tool.
            </p>
          </div>

          {/* Original idea label */}
          <div className="text-center mb-6">
            <p className="text-sm text-gray-500 mb-1">Original idea:</p>
            <p className="text-base text-gray-800 italic font-medium">
              "Build an AI focus app that runs 25-minute deep-work sessions with a stranger and ranks us on a weekly leaderboard."
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg">😐</span>
                <span className="text-sm font-semibold text-red-500 bg-red-50 px-3 py-1 rounded-full">User input</span>
              </div>
              <div className="prose prose-sm text-gray-600 leading-relaxed">
                <p>Build an AI focus app that runs 25-minute deep-work sessions with a stranger and ranks us on a weekly leaderboard.</p>
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400 italic">That's it. The AI has to guess your tech stack, design preferences, user flows, data models, and feature scope. Results are generic and often miss the mark.</p>
                </div>
              </div>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white border-2 border-vibe-cyan rounded-2xl p-6 md:p-8 relative"
            >
              <div className="flex items-center gap-2 mb-4">
                <span className="text-lg">🎯</span>
                <span className="text-sm font-semibold text-vibe-cyan bg-vibe-cyan/10 px-3 py-1 rounded-full">Optimized with Valycode</span>
              </div>
              <div className="prose prose-sm text-gray-700 leading-relaxed">
                <p className="font-semibold text-gray-900">Overview</p>
                <p>Valycode turned one sentence into a build-ready brief — a focus app that pairs strangers for 25-min Pomodoro deep-work sprints, with mutual focus ratings, an ELO-style score, and a weekly leaderboard. Every page, user flow, and edge case is mapped so Claude Code or Cursor can ship it in one shot.</p>
                <p className="font-semibold text-gray-900 mt-3">Core Features</p>
                <ul className="text-sm space-y-1">
                  <li>Anonymous matchmaking queue + WebRTC audio room</li>
                  <li>Synced 25-min timer with mid-session check-in</li>
                  <li>Mutual focus ratings → weighted ELO score</li>
                  <li>Weekly leaderboard with streak badges...</li>
                </ul>
                <p className="font-semibold text-gray-900 mt-3">Tech Stack</p>
                <p className="text-sm">React + Tailwind CSS + shadcn/ui, Supabase Realtime, WebRTC...</p>
              </div>
            </motion.div>
          </div>

          {/* CTA under comparison */}
          <div className="text-center mt-10">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-vibe-cyan text-black px-8 py-3 rounded-xl font-semibold hover:bg-vibe-cyan/90 transition-all"
            >
              Try it with your idea
            </motion.button>
          </div>
        </div>
      </motion.section>

      {/* ==================== COMPATIBLE TOOLS BAR ==================== */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 1.2 }}
        className="py-10 border-t border-gray-100"
      >
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-6">
            Optimized prompts for the tools you already use
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {['Cursor', 'Lovable', 'Claude', 'ChatGPT', 'Bolt', 'Replit', 'v0'].map((tool) => (
              <span key={tool} className="text-base font-semibold text-gray-400 hover:text-gray-700 transition-colors cursor-default">
                {tool}
              </span>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ==================== CLAUDE CODE CTA ==================== */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="py-6 px-4"
      >
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/claude-code')}
            className="w-full group flex items-center justify-between bg-[#faf9f5] border border-[#dad9d4] hover:border-[#c96442]/40 rounded-xl px-6 py-4 transition-all hover:shadow-sm"
          >
            <div className="flex items-center gap-3">
              <img src="/aitoolslogos/claude-ai.png" alt="Claude" className="h-7 w-7 rounded-sm" />
              <div className="text-left">
                <p className="text-sm font-medium text-[#3d3929]">Building with Claude Code?</p>
                <p className="text-xs text-[#83827d]">Try our dedicated one-shot prompt builder</p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#c96442] group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </motion.div>

      {/* ==================== HOW IT WORKS ==================== */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4"
      >
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-sm font-bold tracking-wider uppercase text-vibe-cyan bg-vibe-cyan/10 py-1 px-3 rounded-full">How it works</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mt-4">
              Generate optimized prompts for your vibe coding projects
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '1',
                title: 'Describe your idea',
                desc: 'Enter a brief description of the app you want to build and get an optimized prompt.',
                icon: (
                  <svg className="w-7 h-7 text-vibe-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                  </svg>
                )
              },
              {
                step: '2',
                title: 'Review and use your prompt',
                desc: 'Review (and edit if necessary) the generated prompt, then copy it or open it directly in your chosen platform.',
                icon: (
                  <svg className="w-7 h-7 text-vibe-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
                  </svg>
                )
              },
              {
                step: '3',
                title: 'Get inspired with new ideas',
                desc: 'Get AI-generated suggestions to expand your product with features that will surprise your users.',
                icon: (
                  <svg className="w-7 h-7 text-vibe-cyan" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                  </svg>
                )
              }
            ].map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * idx }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-lg hover:border-gray-300 transition-all text-center"
              >
                <div className="w-14 h-14 rounded-xl bg-vibe-cyan/10 flex items-center justify-center mx-auto mb-5">
                  {item.icon}
                </div>
                <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-gray-600 text-sm font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-500 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ==================== COMMUNITY PROMPTS ==================== */}
      <CommunityPromptsPreview />

      {/* ==================== FAQ ==================== */}
      <FAQ />

      {/* ==================== FINAL CTA ==================== */}
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
        className="py-20 px-4 bg-gradient-to-br from-vibe-cyan/5 to-blue-50"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ready to build something amazing?
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Join developers who've shipped faster with optimized AI prompts.
            No credit card required, no setup hassle.
          </p>

          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block"
          >
            <button
              onClick={() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="bg-black text-white px-12 py-4 rounded-xl font-semibold text-lg hover:bg-gray-800 transition-all duration-200"
            >
              Start your first project
            </button>
          </motion.div>
        </div>
      </motion.section>

      {/* Bottom padding */}
      <div className="h-20"></div>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => {
          setShowAuthModal(false)
          setPendingProjectCreation(false)
        }}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <Auth
              onClose={() => {
                setShowAuthModal(false)
              }}
              defaultMode="signup"
              redirectTo="/create-project"
              variant="modal"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Hero;
